import { useState, useEffect, ChangeEvent, useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import cn from 'classnames';

import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { IKnmErpObjectSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectSelectOption';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { KnmChecklistDropdown } from 'KnmPackage/MainModule/components/KnmChecklistDropdown';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IErvkCheckList } from 'APIPackage/DictionariesModule/interfaces/IErvkCheckList';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { IChecklistAnswerTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IChecklistAnswerTypeSelectOption';

import { IKnmErpFormChecklist } from '../interfaces/IKnmErpFormChecklist';
import { IKnmErpFormChecklistQuestion } from '../interfaces/IKnmErpFormChecklistQuestion';
import styles from './KnmErpChecklist.module.scss';

const PAGE_SIZE = 10;

const getInitialItemsCheck = (questions: IKnmErpFormChecklist['questions']) => {
  const initialItemsCheck: TableCheckItems = {};

  questions?.forEach(question => {
    if (question.listId) {
      initialItemsCheck[question.listId.toString()] = false;
    }
  });

  return initialItemsCheck;
};

interface IKnmErpChecklistProps {
  fieldName: string;
  knmObjectsOptions?: IKnmErpObjectSelectOption[];
  ervkChecklistsSelectOptions: IDictionarySelectOption<IErvkCheckList>[] | undefined;
  checkListsAnswerTypesSelectOptions: IChecklistAnswerTypeSelectOption[] | undefined;
  data: IKnmErpFormChecklist;
  isUseERVKParamTables: boolean;
  watchOnly?: boolean;
}

export const KnmErpChecklist = ({
  fieldName,
  knmObjectsOptions,
  ervkChecklistsSelectOptions,
  checkListsAnswerTypesSelectOptions,
  data,
  isUseERVKParamTables,
  watchOnly,
}: IKnmErpChecklistProps) => {
  const [questionsPage, setQuestionsPage] = useState<number>(0);

  const lastVisibleQuestionNumber = (questionsPage + 1) * PAGE_SIZE;
  const isNextPageAvailable = data.questions && data.questions.length > lastVisibleQuestionNumber;
  const handleLoadButtonClick = useCallback(() => setQuestionsPage(prev => prev + 1), []);

  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>(getInitialItemsCheck(data.questions));
  const [isCheckboxAllChecked, setIsCheckboxAllChecked] = useState(false);

  useEffect(() => {
    setItemsCheck(getInitialItemsCheck(data.questions));
  }, [data.questions]);

  useEffect(() => {
    const keysArray = Object.keys(itemsCheck).filter(itemId => {
      const questionIndex = data.questions ? data.questions.findIndex(question => `${question.listId}` === itemId) : -1;

      return questionIndex >= 0 && questionIndex < lastVisibleQuestionNumber;
    });
    const isEachItemTrue = keysArray.length ? keysArray.every(key => itemsCheck[key]) : false;

    setIsCheckboxAllChecked(isEachItemTrue);
  }, [itemsCheck, data.questions, lastVisibleQuestionNumber]);

  const handleCheckboxAllChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { checked: isChecked } = e.target;

      setItemsCheck(prev => {
        const newItemsCheck: TableCheckItems = { ...prev };

        Object.keys(itemsCheck).forEach(itemId => {
          const questionIndex = data.questions
            ? data.questions.findIndex(question => `${question.listId}` === itemId)
            : -1;

          if (questionIndex >= 0 && questionIndex < lastVisibleQuestionNumber) {
            newItemsCheck[itemId] = isChecked;
          } else {
            newItemsCheck[itemId] = false;
          }
        });

        return newItemsCheck;
      });
    },
    [data.questions, itemsCheck, lastVisibleQuestionNumber],
  );

  const handleCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setItemsCheck({
        ...itemsCheck,
        [e.target.id]: e.target.checked,
      });
    },
    [itemsCheck],
  );

  return (
    <>
      <Row title="Общая информация" />
      <Row>
        <FieldBlock label="Наименование" color="black" placementLabel="left" labelSize="col-4">
          <TextareaField
            className="shared-row-field"
            name={`${fieldName}.name`}
            placeholder="Наименование обязательного требования"
            disabled={watchOnly}
          />
        </FieldBlock>
      </Row>
      {isUseERVKParamTables && (
        <div>
          <Row>
            <FieldBlock label="Номер и название из ЕРВК" color="black" placementLabel="left" labelSize="col-4">
              <SelectInputField
                className="shared-row-field"
                name={`${fieldName}.ervkCheckList`}
                options={ervkChecklistsSelectOptions}
                size="medium"
                disabled={watchOnly}
                validate={makeRequired(REQUIRED_ERROR)}
              />
            </FieldBlock>
          </Row>
        </div>
      )}
      <Row>
        <FieldBlock label="Объект проведения КНМ" color="black" placementLabel="left" labelSize="col-4">
          <SelectInputField
            className="shared-row-field"
            name={`${fieldName}.object`}
            options={knmObjectsOptions}
            size="medium"
            disabled={watchOnly}
          />
        </FieldBlock>
      </Row>

      <div className={styles.Questions}>
        <div className={styles.QuestionsHeader}>
          <Checkbox
            id="questions-check-all"
            theme="system"
            onChange={handleCheckboxAllChange}
            checked={isCheckboxAllChecked}
            disabled={watchOnly}
          />
          <div className={cn('shared-group-label', styles.QuestionsTitle)}>Контрольные вопросы</div>
          <KnmChecklistDropdown
            disabled={!!watchOnly}
            questionAnswerTypeFieldName="answerType"
            itemsCheck={itemsCheck}
            data={data}
            fieldName={fieldName}
          />
        </div>

        <FieldArray name={`${fieldName}.questions`}>
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return (
              <ul className={styles.QuestionsList}>
                {fields.map((name, index) => {
                  if (index > lastVisibleQuestionNumber - 1) {
                    return null;
                  }
                  const question: IKnmErpFormChecklistQuestion = fields.value[index];

                  return (
                    <li className={styles.Question} key={name}>
                      <div className={styles.QuestionCheckboxBlock}>
                        <Checkbox
                          className={styles.QuestionCheckbox}
                          id={question.listId}
                          theme="system"
                          checked={itemsCheck[question.listId] || false}
                          disabled={watchOnly}
                          onChange={handleCheckboxChange}
                        />
                      </div>
                      <div className={styles.QuestionText}>{question.question}</div>
                      <div className={styles.QuestionPropsText}>{question.legalBase}</div>
                      <SelectInputField
                        className={styles.QuestionAnswerTypeSelect}
                        name={`${name}.answerType`}
                        placeholder="Отметка выполнения"
                        options={checkListsAnswerTypesSelectOptions}
                        optionName={CommonCatalogsNamesEnum.CHECKLIST_ANSWER_TYPES}
                        size="medium"
                        disabled={watchOnly}
                      />
                      <div className={styles.AnswerFieldBlock}>
                        {question.answerType?.hasText && (
                          <TextInputField name={`${name}.answer`} placeholder="Введите сведения" disabled={watchOnly} />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          }}
        </FieldArray>
        {isNextPageAvailable && (
          <Button type="button" text="Показать еще" color="load" onClick={handleLoadButtonClick} />
        )}
      </div>
    </>
  );
};
